/**
 * 放送テキストに SSML 辞書を適用した SSML を作る (SBI-003 辞書適用Audio作成)。
 *
 * ## なぜ画面側で辞書を再実装しないか
 *
 * 辞書適用 (読み仮名辞典 / アクセント辞典 / 長い文字列優先 / タグ内は置換しない) は
 * サーバ側 `lambda/common/ssml_dictionary.py` が正源で、UpdateContents (本番) と
 * `POST /audio-preview` が同じ関数を呼ぶ。画面側にも実装すると「プレビューと本番で
 * 置換結果が食い違う」事故の温床になるため、**既存の `POST /audio-preview` を無改変で使い**、
 * 応答の `applied_ssml` (辞書適用後) を受け取る。
 *
 * ## 送り方
 *
 * プレーンテキストを `& < >` エスケープして `<speak>` で囲み (`buildPreviewSsml`)、
 * `content` (free_text / `input_mode: 'ssml'`) として渡す。`content` 指定のときだけ
 * サーバが `rendered_ssml` (置換前) / `applied_ssml` (置換後) を返すため。
 *
 *   - 辞書が 1 件以上効いた … `applied_ssml` が付く → それを Step2 に出す
 *   - 辞書が効かなかった   … `applied_ssml` は付かない → 置換前 (`rendered_ssml`) を出す
 *
 * ⚠️ サーバは Polly 合成まで行う (応答の音声は本関数では使わない)。
 *   押すたびに Polly 課金が発生するため、呼び出し側は自動実行せず、利用者の操作でのみ呼ぶこと。
 */
import { SSML_TUNING_LANGUAGE_KEY, SSML_TUNING_VOICE_ID } from './constants';
import { buildPreviewSsml } from './ssmlDraft';

/**
 * 辞書適用を実行する関数の型 (`BroadcastPointsClient.previewAudio` を `content` 付きで包んで注入する)。
 *
 * 戻り値は `AudioPreviewResponse` の部分集合 (使うのは SSML の 2 項目だけ)。
 */
export type ApplyDictionaryFn = (payload: {
  /** `<speak>…</speak>` (放送テキストをエスケープして囲んだもの) */
  ssml: string;
  voiceId: string;
  language: string;
}) => Promise<{
  /** 辞書適用前の SSML */
  rendered_ssml?: string;
  /** 辞書適用後の SSML (辞書が 1 件以上効いたときのみ) */
  applied_ssml?: string;
}>;

/** {@link createDictionarySsml} の結果。 */
export type CreateDictionarySsmlResult =
  | {
      ok: true;
      /** Step2 に表示する SSML (辞書適用済み。辞書が効かなければ置換前と同じ) */
      ssml: string;
      /** 辞書で 1 件以上置換されたか (「該当する語が無かった」を利用者へ伝えるため) */
      dictionaryApplied: boolean;
    }
  | { ok: false; error: string };

/**
 * 放送テキストに SSML 辞書を適用した SSML を作る。
 *
 * 失敗しても例外は投げず `{ ok: false, error }` で返す
 * (サーバのメッセージ — 文字数超過の 400 など — をそのまま利用者へ見せるため)。
 *
 * @param text Step1 の放送テキスト (プレーンテキスト)
 * @param applyDictionary 辞書適用の実行関数 (テストで差し替えられるよう注入で受ける)
 */
export async function createDictionarySsml(
  text: string,
  applyDictionary: ApplyDictionaryFn,
): Promise<CreateDictionarySsmlResult> {
  const prepared = buildPreviewSsml(text);
  if (!prepared.ok) {
    return { ok: false, error: prepared.error };
  }

  try {
    const response = await applyDictionary({
      ssml: prepared.ssml,
      // Provider / Voice は選択させない (要件で Polly / Takumi 固定)。
      voiceId: SSML_TUNING_VOICE_ID,
      language: SSML_TUNING_LANGUAGE_KEY,
    });
    const applied = response.applied_ssml;
    if (typeof applied === 'string' && applied !== '') {
      return { ok: true, ssml: applied, dictionaryApplied: true };
    }
    // 辞書が効かなかった (または旧 Lambda で項目が無い) ときは、置換前をそのまま見せる。
    const rendered = response.rendered_ssml;
    return {
      ok: true,
      ssml: typeof rendered === 'string' && rendered !== '' ? rendered : prepared.ssml,
      dictionaryApplied: false,
    };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : '辞書適用 SSML の作成に失敗しました',
    };
  }
}
